import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithProvider } from '../services/authService';
import { CANCELLED_CODES, REASONS, clearSignInPending, guardRefusal, signInTroubleCopy } from '../services/signInTrouble';
import { emailSignInAvailable, loadEmailSignInAvailability } from '../services/emailLinkSignIn';
import { offerEmailAfterBack, openAccountExistsSheet, openRefusedSheet, openSheetForSignIn } from '../services/signInSheet';
import { postSignInDestination, rememberDestination } from '../utils/postSignIn';
import { hasCarry } from '../services/astraCarry';
import { ASTRA_PATH } from '../utils/astraLink';
import useSignInNotice from './useSignInNotice';
import { MOMENT_ENTRIES } from '../content/signupMoments';

// Signed in from the landing chat's own Start free (astra_chat_*) with its
// conversation still in this tab: land in Astra, where that conversation
// continues (#2945). sessionStorage, so it holds across the phone's redirect
// sign-in too, and the email link copies it (#3928). The unlock box under a
// locked read (#3480) lands there too: the full read opens as that
// conversation's first card (mock B2).
//
// A sign-up moment's button (#5195, signup_moments) lands on /start instead:
// the schools it offered and the GPA are filled in there, the first read of
// the school is the "why", and the Targets are one tap away after it. This
// rides profile_first_signup and first_read_onboarding (both on for everyone
// since Oct 22): without them /start sends a new account to /profile or
// /welcome, and a returning student goes to /launchpad, not Astra. The
// carried read and conversation still reach Astra when it opens.
export const MOMENT_DESTINATION = '/start';
function rememberCarryDestination(entry) {
    if (typeof entry === 'string' && MOMENT_ENTRIES.includes(entry)) {
        rememberDestination(MOMENT_DESTINATION);
        return;
    }
    if (typeof entry === 'string' && (entry.startsWith('astra_chat_') || entry === 'astra_read_unlock')
        && hasCarry()) {
        rememberDestination(ASTRA_PATH);
    }
}

// Google sign-in with VISIBLE progress. The silent gap the founder hit
// (2026-07-18): the popup resolves, then postSignInDestination waits on a
// cold-started backend for seconds while the marketing page just sits there —
// reads as "nothing happened". Phase drives the overlay copy:
//   'popup'     — Google's window is up (it may cover ours)
//   'preparing' — popup done, destination lookup in flight (the cold start)
export default function useGoogleSignIn() {
    const navigate = useNavigate();
    const notify = useSignInNotice();
    const [phase, setPhase] = useState(null);   // null | 'popup' | 'preparing'
    const providerRef = useRef(null);
    // The last Start's moment words (#5195), so the recovery sheet after a
    // Google sign-in that did not finish still names the payoff.
    const wordsRef = useRef(null);

    // iOS signs in by redirect, whose promise never settles: the page is
    // meant to unload. A back-swipe from Google can restore THIS page from
    // Safari's back-forward cache with the overlay still up and the button
    // guarded shut (reviewer, #2457). A persisted pageshow means the
    // student came back on purpose without signing in: reset, quietly.
    useEffect(() => {
        const onShow = (e) => {
            if (!e.persisted) return;
            clearSignInPending();
            setPhase(null);
        };
        window.addEventListener('pageshow', onShow);
        return () => window.removeEventListener('pageshow', onShow);
    }, []);

    // Whether the sheet may offer the email link (#3928): asked once per
    // page life, so the answer is known by the time anyone taps Start free.
    useEffect(() => { loadEmailSignInAvailability(); }, []);

    // `entry`: the landing watch's id for what started it (content/
    // landingIds.js SIGNIN_ENTRIES). Wired straight to onClick it is a click
    // event, which records as 'other'.
    // signInProvider is the provider itself, never the sheet: the sheet's
    // own buttons call it, straight from the click (#5154: Google, Apple or
    // Facebook; `position` is the button's place on the sheet).
    const signInProvider = useCallback(async (providerId, entry, position) => {
        if (phase) return;                       // one flight at a time
        rememberCarryDestination(entry);
        setPhase('popup');
        const id = typeof entry === 'string' ? entry : undefined;
        const again = (p, pos) => providerRef.current?.(p, id, pos);
        try {
            const user = await signInWithProvider(providerId, { entry: id, position });
            setPhase('preparing');
            navigate(await postSignInDestination(user?.email));
        } catch (error) {
            setPhase(null);
            // This email already has an account (#5154): the sheet that
            // offers the ways that always reach it.
            if (error?.accountExists) {
                openAccountExistsSheet({ entry: id, info: error.accountExists, onProvider: again });
                return;
            }
            // The password guard refused this provider (a Facebook account
            // with no email): its words and the other ways in.
            if (guardRefusal(error) === REASONS.NO_EMAIL) {
                openRefusedSheet({ entry: id, provider: providerId, notice: signInTroubleCopy(error).message, onProvider: again });
                return;
            }
            // A cancellation is a user action — quiet. A REAL failure must be
            // VISIBLE, with a way back (digest S1, 2026-07-30: a user whose
            // extension blocks Google's identity API clicked sign-in 49 times
            // against a silently resetting button; #2457: the retry is a
            // button on the notice, not a hunt for the CTA).
            if (!notify(error, () => providerRef.current?.(providerId, 'retry'))) {
                console.info('Sign in cancelled:', error?.code);
                // Closed the provider's window without signing in: for a
                // student whose school blocks Google, the email is the way in
                // (#3928); "Try <provider> again" stays on it.
                if (CANCELLED_CODES.includes(error?.code) && emailSignInAvailable()) {
                    offerEmailAfterBack({ entry: id, retry: providerId, lead: wordsRef.current?.lead || null,
                        onProvider: (p, pos) => providerRef.current?.(p, 'retry', pos),
                        onGoogle: () => providerRef.current?.('google', 'retry') });
                }
            } else {
                console.error('Sign in failed:', error);
            }
        }
    }, [phase, navigate, notify]);
    providerRef.current = signInProvider;
    // Google itself, as before #5154.
    const signInGoogle = useCallback((entry) => signInProvider('google', entry), [signInProvider]);

    // What every Start free calls. With the email link on (#3928, founder
    // pick A) it opens the sheet: Google first, email one tap away; on iPhone
    // and iPad Safari with phone_email_first on, the email first (#5084);
    // inside an app's browser, where Google refuses sign-in, the email leads
    // and Google is never tried, even on a tap before the answer on the email
    // link has arrived (#5084, invariant 18). Otherwise Google, exactly as
    // before.
    // `words` (#5195): {title, lead}, a sign-up moment's own button words
    // and payoff, which the sheet repeats in every mode in place of "Start
    // free" (only the words change; each mode keeps its #5084 / #5154 rules).
    const signIn = useCallback(async (entry, words = null) => {
        if (phase) return undefined;
        const id = typeof entry === 'string' ? entry : undefined;
        const title = words && typeof words.title === 'string' ? words.title : null;
        const lead = words && typeof words.lead === 'string' ? words.lead : null;
        wordsRef.current = title || lead ? { title, lead } : null;
        const opened = openSheetForSignIn({ entry: id, title, lead, everyMode: Boolean(title || lead),
            onProvider: (p, pos) => providerRef.current?.(p, id, pos),
            onGoogle: () => providerRef.current?.('google', id),
            onEmailOff: () => providerRef.current?.('google', id) });
        if (!opened) return signInGoogle(entry);
        rememberCarryDestination(id);
        return undefined;
    }, [phase, signInGoogle]);

    return { signIn, signInGoogle, signInProvider, signingIn: phase !== null, phase };
}
